import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { consignmentsApi, type CreateConsignmentInput } from "./api";

export const consignmentKeys = {
  all: ["consignments"] as const,
  lists: () => [...consignmentKeys.all, "list"] as const,
  list: (status: "open" | "converted" | "discarded") => [...consignmentKeys.lists(), status] as const,
  detail: (id: number) => [...consignmentKeys.all, "detail", id] as const,
};

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: consignmentKeys.all });
  qc.invalidateQueries({ queryKey: ["sales"] });
  qc.invalidateQueries({ queryKey: ["summary"] });
  qc.invalidateQueries({ queryKey: ["customers"] });
}

export function useConsignments(status: "open" | "converted" | "discarded" = "open") {
  return useQuery({
    queryKey: consignmentKeys.list(status),
    queryFn: () => consignmentsApi.list(status),
  });
}

export function useConsignment(id: number | null) {
  return useQuery({
    queryKey: consignmentKeys.detail(id ?? -1),
    queryFn: () => consignmentsApi.get(id!),
    enabled: id != null,
  });
}

export function useCreateConsignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateConsignmentInput) => consignmentsApi.create(body),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateConsignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof consignmentsApi.update>[1] }) => consignmentsApi.update(id, body),
    onSuccess: (data) => {
      qc.setQueryData(consignmentKeys.detail(data.id), data);
      invalidate(qc);
    },
  });
}

export function useConvertConsignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof consignmentsApi.convert>[1] }) => consignmentsApi.convert(id, body),
    onSuccess: (data) => {
      qc.setQueryData(consignmentKeys.detail(data.id), data);
      invalidate(qc);
    },
  });
}

export function useRefundConsignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof consignmentsApi.refund>[1] }) => consignmentsApi.refund(id, body),
    onSuccess: (data) => {
      qc.setQueryData(consignmentKeys.detail(data.id), data);
      invalidate(qc);
    },
  });
}

export function useDiscardConsignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => consignmentsApi.discard(id),
    onSuccess: (data) => {
      qc.setQueryData(consignmentKeys.detail(data.id), data);
      invalidate(qc);
    },
  });
}
